import { SearchIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import i18n from '@/utils/i18n';
import { MIN_SEARCH_LENGTH } from './usePickingJobList';

interface PickedItemSearchProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * "Where is this box?" — searches the jobs by what was scanned onto them. The
 * matched fields are documented on getPickingJobs' picked_item_search in
 * colugo's Picking schema; the "Box ID" of the placeholder is the container
 * ShortId.
 *
 * Names are not unique, so this narrows the list instead of jumping to one job
 * — two jobs can legitimately hold a container of the same name. Terms shorter
 * than MIN_SEARCH_LENGTH are not sent, and the hint says so.
 *
 * Jobs that have not been started have nothing scanned on them, so they drop out
 * of the list while a search is active.
 *
 * A search against the default status filter widens to every status; see
 * usePickingJobList, which also raises the hint for it.
 */
export function PickedItemSearch({ value, onChange }: PickedItemSearchProps) {
  const trimmedLength = value.trim().length;
  const tooShort = trimmedLength > 0 && trimmedLength < MIN_SEARCH_LENGTH;
  return (
    <div className="tw:flex tw:items-center tw:gap-2">
      <div className="tw:relative">
        <SearchIcon className="tw:absolute tw:left-2 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
        <Input
          className="tw:w-[260px] tw:pl-8"
          placeholder={i18n.t('Picking:searchPlaceholder')}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
      {tooShort && (
        <span className="tw:text-xs tw:text-muted-foreground">
          {i18n.t('Picking:searchMinLength', { count: MIN_SEARCH_LENGTH })}
        </span>
      )}
      {value.length > 0 && (
        <Button variant="ghost" size="icon" onClick={() => onChange('')}>
          <XIcon />
          <span className="tw:sr-only">{i18n.t('Picking:searchClear')}</span>
        </Button>
      )}
    </div>
  );
}
